// Web Audio engine for the /home-key course: soft dream-pop voices, a drone
// bed, and free-time phrase playback. Native Web Audio only — same lightweight
// posture as synthLab.js / keyboardPlan.js, but self-contained: this sub-site
// owns its audio (site convention), it only borrows their *patterns* (lazy
// shared AudioContext, osc → filter → ADSR voice, one master gain bus).
//
// Everything is guarded so importing this module (and even calling into it)
// in a context with no AudioContext — Jest's jsdom, SSR — never throws: the
// context is only created lazily on the first play call.

import { midiToFreq, phraseLength } from "./homeKeyTheory";

/* ============================================================
   Context + master bus
   ============================================================ */

let audioCtx = null;
let masterGain = null;
let masterVolume = 0.9;

// Lazily create (and resume) the single shared AudioContext. Must first be
// reached from a user gesture so browsers allow playback. Null when the
// environment has no Web Audio (jsdom, old browsers).
export const getCtx = () => {
  if (typeof window === "undefined") return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!audioCtx) audioCtx = new AC();
  if (audioCtx.state === "suspended") audioCtx.resume();
  return audioCtx;
};

// Call from the first user gesture (a "begin" button) so iOS/Chrome unmute.
export const resumeAudio = () => {
  const ctx = getCtx();
  if (ctx && ctx.state === "suspended") ctx.resume();
  return Boolean(ctx);
};

// Every voice routes through this one gain into the destination.
const getMaster = (ctx) => {
  if (!masterGain) {
    masterGain = ctx.createGain();
    masterGain.gain.value = masterVolume;
    masterGain.connect(ctx.destination);
  }
  return masterGain;
};

export const setMasterVolume = (v) => {
  masterVolume = Math.min(1, Math.max(0, Number(v) || 0));
  if (masterGain && audioCtx) {
    masterGain.gain.setTargetAtTime(masterVolume, audioCtx.currentTime, 0.03);
  }
};

// The master bus itself (lazily created with the context) — homeKeyGroove's
// drum voices route through this so one volume knob governs the whole track.
// Null when the environment has no Web Audio (jsdom, SSR).
export const getMasterBus = () => {
  const ctx = getCtx();
  return ctx ? getMaster(ctx) : null;
};

/* ============================================================
   Patches — plain data describing a voice: layered oscillators
   (each with optional detune cents / octave / semitone offset),
   one lowpass filter, and an ADSR amp envelope.
   ============================================================ */

export const PATCHES = {
  // The dreamy bed for demos: two gently detuned waves, slow swell, hazy top.
  dreamPad: {
    id: "dreamPad",
    oscs: [
      { type: "triangle", detune: -6, gain: 0.65 },
      { type: "sawtooth", detune: 6, gain: 0.3 },
    ],
    filter: { frequency: 1200, Q: 0.7 },
    env: { attack: 0.4, decay: 0.5, sustain: 0.8, release: 1.6 },
    peak: 0.16,
  },
  // The playing voice: warm keys, immediate but soft-edged.
  softKeys: {
    id: "softKeys",
    oscs: [
      { type: "triangle", detune: 0, gain: 0.85 },
      { type: "sine", octave: 1, gain: 0.07 },
    ],
    filter: { frequency: 2600, Q: 0.6 },
    env: { attack: 0.008, decay: 0.28, sustain: 0.55, release: 0.5 },
    peak: 0.2,
  },
  // The drone: deep and very slow, sub underneath, darkness on top.
  droneBed: {
    id: "droneBed",
    oscs: [
      { type: "sine", octave: -1, gain: 0.8 },
      { type: "triangle", detune: -4, gain: 0.5 },
      { type: "sawtooth", detune: 5, gain: 0.18 },
    ],
    filter: { frequency: 600, Q: 0.5 },
    env: { attack: 2.2, decay: 1.0, sustain: 0.9, release: 2.4 },
    peak: 0.12,
  },
};

// Lesson data references patches by name (serializable); code may pass the
// object directly. Either resolves here; unknown names fall back to softKeys.
const resolvePatch = (patch) => {
  if (patch && typeof patch === "object") return patch;
  return PATCHES[patch] || PATCHES.softKeys;
};

/* ============================================================
   Voice construction (osc layers → lowpass → ADSR amp → master)
   ============================================================ */

// Build one voice's node graph and schedule its attack/decay from t0.
// `dest` defaults to the master bus. Returns the handles needed to release it.
const buildVoice = (ctx, patch, midi, t0, dest, velocity = 1) => {
  const p = resolvePatch(patch);
  const vel = Math.min(1, Math.max(0.05, velocity));

  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = (p.filter && p.filter.frequency) || 2000;
  filter.Q.value = (p.filter && p.filter.Q) || 0.7;

  const amp = ctx.createGain();
  amp.gain.setValueAtTime(0.0001, t0);
  filter.connect(amp);
  amp.connect(dest || getMaster(ctx));

  const oscs = (p.oscs || [{ type: "triangle", gain: 1 }]).map((layer) => {
    const osc = ctx.createOscillator();
    osc.type = layer.type || "triangle";
    osc.frequency.value = midiToFreq(
      midi + (layer.semis || 0) + 12 * (layer.octave || 0)
    );
    if (layer.detune) osc.detune.value = layer.detune;
    const g = ctx.createGain();
    g.gain.value = layer.gain == null ? 1 : layer.gain;
    osc.connect(g);
    g.connect(filter);
    osc.start(t0);
    return osc;
  });

  const env = p.env || {};
  const a = Math.max(0.003, env.attack || 0.01);
  const d = Math.max(0.01, env.decay || 0.2);
  const peak = (p.peak || 0.18) * vel;
  const susLevel = Math.max(0.0001, peak * (env.sustain == null ? 0.7 : env.sustain));
  amp.gain.exponentialRampToValueAtTime(peak, t0 + a);
  amp.gain.exponentialRampToValueAtTime(susLevel, t0 + a + d);

  return {
    oscs,
    amp,
    release: Math.max(0.03, env.release || 0.3),
    t0,
    attack: a,
    decay: d,
    peak,
    susLevel,
  };
};

// What the scheduled ADSR is worth at time t (before any release) — lets a
// release scheduled mid-attack or mid-decay hand off without a click or a
// dropout. Linear approximations of the exponential ramps are fine here.
const envValueAt = (voice, t) => {
  const { t0, attack, decay, peak, susLevel } = voice;
  if (t <= t0) return 0.0001;
  if (t < t0 + attack) return Math.max(0.0001, (peak * (t - t0)) / attack);
  if (t < t0 + attack + decay) {
    const frac = (t - t0 - attack) / decay;
    return Math.max(0.0001, peak + (susLevel - peak) * frac);
  }
  return susLevel;
};

// Trigger a voice's release at `when` and stop its oscillators after the tail.
const releaseVoice = (ctx, voice, when) => {
  const t = Math.max(when, ctx.currentTime);
  // This may be scheduled long before the note even starts (phrase playback),
  // when gain.value still reads as silence — never sample .value here. Pin
  // the envelope's computed value at t, then ramp out from it.
  voice.amp.gain.cancelScheduledValues(t);
  voice.amp.gain.setValueAtTime(envValueAt(voice, t), t);
  voice.amp.gain.exponentialRampToValueAtTime(0.0001, t + voice.release);
  voice.oscs.forEach((osc) => osc.stop(t + voice.release + 0.05));
};

const NOOP_HANDLE = { stop: () => {} };

/* ============================================================
   Public playing API
   ============================================================ */

// Start a held note; the returned stop() triggers the ADSR release.
export const startNote = (patch, midi, velocity = 1) => {
  const ctx = getCtx();
  if (!ctx) return NOOP_HANDLE;
  const voice = buildVoice(ctx, patch, midi, ctx.currentTime, null, velocity);
  let stopped = false;
  return {
    stop: () => {
      if (stopped) return;
      stopped = true;
      releaseVoice(ctx, voice, ctx.currentTime);
    },
  };
};

// Fire-and-forget: play a note for durMs, optionally whenMs from now.
export const playNote = (patch, midi, durMs = 600, whenMs = 0, velocity = 1) => {
  const ctx = getCtx();
  if (!ctx) return;
  const t0 = ctx.currentTime + Math.max(0, whenMs) / 1000;
  const voice = buildVoice(ctx, patch, midi, t0, null, velocity);
  releaseVoice(ctx, voice, t0 + Math.max(0.05, durMs / 1000));
};

// The bed under everything: a droneBed voice on the root (G2 by default) plus
// a soft fifth above, with a slow amplitude LFO so it breathes. Returns
// { stop() } — stop fades the whole bed out over the drone's long release.
export const startDrone = (midi = 43) => {
  const ctx = getCtx();
  if (!ctx) return NOOP_HANDLE;
  const t0 = ctx.currentTime;

  // The drone gets its own sub-bus so the LFO wobbles root + fifth together.
  const bed = ctx.createGain();
  bed.gain.value = 1;
  bed.connect(getMaster(ctx));

  const root = buildVoice(ctx, PATCHES.droneBed, midi, t0, bed, 1);
  const fifth = buildVoice(ctx, PATCHES.droneBed, midi + 7, t0, bed, 0.4);

  // Gentle breathing: ±~6% at 0.13 Hz on the bed gain.
  const lfo = ctx.createOscillator();
  lfo.type = "sine";
  lfo.frequency.value = 0.13;
  const lfoDepth = ctx.createGain();
  lfoDepth.gain.value = 0.06;
  lfo.connect(lfoDepth);
  lfoDepth.connect(bed.gain);
  lfo.start(t0);

  let stopped = false;
  return {
    stop: () => {
      if (stopped) return;
      stopped = true;
      const t = ctx.currentTime;
      releaseVoice(ctx, root, t);
      releaseVoice(ctx, fifth, t);
      lfo.stop(t + root.release + 0.1);
    },
  };
};

// Play a whole phrase ({ notes: [{ midi, at, dur, vel? }] }) verbatim,
// starting shortly after now. Returns { stop, durMs } — durMs covers the last
// note's ring-out so callers can time "done" states; stop() cuts every voice
// (scheduled-but-unstarted ones included) with a quick fade.
export const playPhrase = (phrase, patch = PATCHES.softKeys, opts = {}) => {
  const p = resolvePatch(patch);
  const delayMs = opts.delayMs == null ? 50 : opts.delayMs;
  const gain = opts.gain == null ? 1 : opts.gain;
  const releaseMs = ((p.env && p.env.release) || 0.3) * 1000;
  const durMs = delayMs + phraseLength(phrase) + releaseMs;

  const ctx = getCtx();
  const notes = (phrase && phrase.notes) || [];
  if (!ctx || !notes.length) return { stop: () => {}, durMs };

  const base = ctx.currentTime + delayMs / 1000;
  const voices = notes.map((n) => {
    const t0 = base + (n.at || 0) / 1000;
    const voice = buildVoice(ctx, p, n.midi, t0, null, (n.vel == null ? 1 : n.vel) * gain);
    releaseVoice(ctx, voice, t0 + Math.max(0.05, (n.dur || 400) / 1000));
    return voice;
  });

  let stopped = false;
  return {
    stop: () => {
      if (stopped) return;
      stopped = true;
      const t = ctx.currentTime;
      voices.forEach((voice) => {
        voice.amp.gain.cancelScheduledValues(t);
        voice.amp.gain.setTargetAtTime(0.0001, t, 0.04);
        voice.oscs.forEach((osc) => osc.stop(t + 0.25));
      });
    },
    durMs,
  };
};

// Loop a phrase back to back `times` times with a small breath (`gapMs`)
// between passes — the progression builder's play button and the Studio's
// chords row. Builds one long phrase by offsetting each pass's `at`s and
// hands it to playPhrase, so stop() cuts everything (unstarted passes
// included) and durMs covers the whole run.
export const playPhraseLoop = (phrase, opts = {}) => {
  const times = Math.max(1, Math.floor(opts.times == null ? 4 : opts.times));
  const gapMs = Math.max(0, opts.gapMs == null ? 120 : opts.gapMs);
  const notes = (phrase && phrase.notes) || [];
  const passMs = phraseLength(phrase) + gapMs;
  const looped = {
    notes: Array.from({ length: times }, (_, pass) =>
      notes.map((n) => ({ ...n, at: (n.at || 0) + pass * passMs }))
    ).flat(),
  };
  return playPhrase(looped, opts.patch, { gain: opts.gain });
};

// Layered playback — e.g. drone + a saved motif = "your track so far".
// `list` entries: { phrase, patch?, opts? } (or a bare phrase). Returns one
// combined { stop, durMs }.
export const playPhrases = (list = []) => {
  const handles = list
    .filter(Boolean)
    .map((item) =>
      item.notes
        ? playPhrase(item)
        : playPhrase(item.phrase, item.patch, item.opts)
    );
  return {
    stop: () => handles.forEach((h) => h.stop()),
    durMs: handles.reduce((max, h) => Math.max(max, h.durMs), 0),
  };
};
